import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PettyCash.css';
import { formatCurrency } from '../utils/formatters';

const Icon = ({ d, size = 16, strokeWidth = 2, color = 'currentColor', style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0, ...style }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    x: ['M18 6L6 18', 'M6 6l12 12'],
    wallet: ['M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4', 'M4 6v12c0 1.1.9 2 2 2h14v-4', 'M18 12a2 2 0 0 0-2 2c0 1.1.1 2 2 2h4v-4h-4z'],
    history: ['M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8', 'M3 3v5h5', 'M12 7v5l4 2'],
    export: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
    import: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M17 8l-5-5-5 5', 'M12 3v12'],
    card: ['M2 10h20', 'M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z'],
    receipt: ['M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z', 'M16 8h-4', 'M16 12h-4', 'M8 8h.01', 'M8 12h.01'],
    swap: ['M7 10v4h3', 'm7 10 3 3', 'M17 14v-4h-3', 'm17 14-3-3'],
    settings: ['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z'],
    building: ['M3 21h18', 'M3 7v14m18-14v14', 'M3 7l9-4 9 4', 'M5 21V11m4 10V11m6 10V11m4 10V11'],
    home: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10']
};

export default function PettyCash({ user, operatorDetails }) {
    const [accounts, setAccounts] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // 'add-account', 'add-transaction'
    const [accountNames, setAccountNames] = useState([]);
    const [locations, setLocations] = useState([]);
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [staff, setStaff] = useState([]);
    const [accountSearch, setAccountSearch] = useState('');
    const [transSearch, setTransSearch] = useState('');

    const operatorId = user?.operatorId || 'all';
    const country = operatorDetails?.country;

    useEffect(() => {
        fetchData();
        fetchSettings();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [accRes, transRes, staffRes] = await Promise.all([
                axios.get(`/api/petty-cash/accounts/${operatorId}`).catch(() => ({ data: [] })),
                axios.get(`/api/petty-cash/transactions/${operatorId}`).catch(() => ({ data: [] })),
                axios.get(`/api/users/${operatorId}`).catch(() => ({ data: [] }))
            ]);
            setAccounts(Array.isArray(accRes.data) ? accRes.data : []);
            setTransactions(Array.isArray(transRes.data) ? transRes.data : []);
            setStaff(Array.isArray(staffRes.data) ? staffRes.data : []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const res = await axios.get('/api/master-data');
            const data = Array.isArray(res.data) ? res.data : [];
            setAccountNames(data.filter(d => d.category === 'petty-cash-account-names' && d.active));
            setLocations(data.filter(d => d.category === 'petty-cash-locations' && d.active));
            setTransactionTypes(data.filter(d => d.category === 'petty-cash-transaction-types' && d.active));
        } catch (err) {
            console.error(err);
        }
    };

    const handleAddAccount = async (formData) => {
        try {
            await axios.post('/api/petty-cash/accounts', { ...formData, operatorId });
            setModal(null);
            fetchData();
        } catch (err) {
            alert(err.response?.data?.message || 'Error creating account');
        }
    };

    const handleAddTransaction = async (formData) => {
        try {
            await axios.post('/api/petty-cash/transactions', { ...formData, operatorId });
            setModal(null);
            fetchData();
        } catch (err) {
            alert(err.response?.data?.message || 'Error recording transaction');
        }
    };

    const exportToCSV = () => {
        if (!transactions.length) {
            alert('No transactions to export');
            return;
        }
        const headers = ['Date', 'Account', 'Type', 'Amount', 'Balance After', 'Handler', 'Description'];
        const rows = transactions.map(t => [
            t.date ? t.date.split('T')[0] : '',
            `"${(t.accountId?.accountName || '').replace(/"/g, '""')}"`,
            t.type || '',
            t.amount || 0,
            t.balanceAfter || 0,
            `"${(t.handlerId?.firstName || '')} ${(t.handlerId?.lastName || '')}"`,
            `"${(t.description || '').replace(/"/g, '""')}"`
        ]);

        const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `Petty_Cash_Transactions_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const totalBalance = accounts.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);
    const totalExpenses = transactions.filter(t => t.type === 'Withdrawal' || t.type === 'Adjustment (Out)').reduce((sum, t) => sum + (t.amount || 0), 0);

    const filteredAccounts = accounts.filter(acc =>
        !accountSearch ||
        acc.accountName?.toLowerCase().includes(accountSearch.toLowerCase()) ||
        acc.location?.toLowerCase().includes(accountSearch.toLowerCase())
    );

    const filteredTransactions = transactions.filter(t =>
        !transSearch ||
        t.accountId?.accountName?.toLowerCase().includes(transSearch.toLowerCase()) ||
        t.description?.toLowerCase().includes(transSearch.toLowerCase()) ||
        t.type?.toLowerCase().includes(transSearch.toLowerCase())
    );

    return (
        <div className="pc-container">
            {/* Breadcrumb */}
            <div className="pc-breadcrumb">
                <span className="home-icon-svg"><Icon d={icons.home} size={15} color="#64748b" /></span>
                <span className="sep">›</span>
                <span>Finance</span>
                <span className="sep">›</span>
                <span className="current">Petty Cash</span>
            </div>

            {/* Header */}
            <header className="pc-header">
                <div>
                    <h1 className="pc-title">Petty Cash Management</h1>
                    <p className="pc-subtitle">Track and manage petty cash floats, disbursements, and balances across locations.</p>
                </div>
                <div className="pc-header-actions">
                    <button className="pc-btn-secondary" onClick={() => window.location.hash = '#petty-cash-setup'} title="Configure accounts & locations">
                        <Icon d={icons.settings} size={15} color="#475569" />
                        <span>Configure Settings</span>
                    </button>
                    <button className="pc-btn-secondary" onClick={exportToCSV} title="Export transaction log">
                        <Icon d={icons.export} size={15} color="#475569" />
                        <span>Export CSV</span>
                    </button>
                    <button className="pc-btn-primary" onClick={() => setModal('add-account')}>
                        <Icon d={icons.plus} size={16} strokeWidth={2.5} color="#fff" />
                        <span>Add Petty Cash Account</span>
                    </button>
                </div>
            </header>

            {/* KPI Cards */}
            <div className="pc-stats-grid">
                <div className="pc-stat-card">
                    <div className="pc-stat-inner">
                        <div>
                            <span className="pc-stat-label">Total Balance</span>
                            <div className="pc-stat-val">{formatCurrency(totalBalance, country)}</div>
                            <span className="pc-stat-sub">{accounts.length} active float{accounts.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="pc-stat-icon-wrap badge-blue">
                            <Icon d={icons.wallet} size={20} color="#1d4ed8" />
                        </div>
                    </div>
                </div>

                <div className="pc-stat-card">
                    <div className="pc-stat-inner">
                        <div>
                            <span className="pc-stat-label">Active Accounts</span>
                            <div className="pc-stat-val">{accounts.filter(a => a.status === 'Active').length}</div>
                            <span className="pc-stat-sub">In operation</span>
                        </div>
                        <div className="pc-stat-icon-wrap badge-emerald">
                            <Icon d={icons.card} size={20} color="#059669" />
                        </div>
                    </div>
                </div>

                <div className="pc-stat-card">
                    <div className="pc-stat-inner">
                        <div>
                            <span className="pc-stat-label">Total Expenses</span>
                            <div className="pc-stat-val">{formatCurrency(totalExpenses, country)}</div>
                            <span className="pc-stat-sub">Disbursed cash</span>
                        </div>
                        <div className="pc-stat-icon-wrap badge-cyan">
                            <Icon d={icons.receipt} size={20} color="#0284c7" />
                        </div>
                    </div>
                </div>

                <div className="pc-stat-card">
                    <div className="pc-stat-inner">
                        <div>
                            <span className="pc-stat-label">Transactions</span>
                            <div className="pc-stat-val">{transactions.length}</div>
                            <span className="pc-stat-sub">Total recorded logs</span>
                        </div>
                        <div className="pc-stat-icon-wrap badge-indigo">
                            <Icon d={icons.swap} size={20} color="#16539C" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Accounts Panel */}
            <div className="pc-panel">
                <div className="pc-panel-header">
                    <h3 className="pc-panel-title">Petty Cash Accounts</h3>
                    <div className="pc-search-wrap">
                        <Icon d={icons.search} size={15} />
                        <input
                            className="pc-search-input"
                            placeholder="Search accounts by name or location..."
                            value={accountSearch}
                            onChange={e => setAccountSearch(e.target.value)}
                        />
                    </div>
                </div>
                <div className="pc-table-wrap">
                    <table className="pc-table">
                        <thead>
                            <tr>
                                <th>Account Name</th>
                                <th>Location</th>
                                <th>Handler / Custodian</th>
                                <th>Current Balance</th>
                                <th>Fund Limit</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="pc-empty-state">
                                        <p>Loading accounts...</p>
                                    </td>
                                </tr>
                            ) : filteredAccounts.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="pc-empty-state">
                                        <div className="pc-empty-icon">
                                            <Icon d={icons.wallet} size={30} color="#16539C" />
                                        </div>
                                        <h3 className="pc-empty-title">No petty cash accounts configured yet</h3>
                                        <p className="pc-empty-desc">Set up your petty cash floats and custodians to track cash expenses.</p>
                                        <div className="pc-empty-actions">
                                            <button className="pc-btn-primary" onClick={() => setModal('add-account')}>
                                                <Icon d={icons.plus} size={16} strokeWidth={2.5} color="#fff" />
                                                <span>Add Petty Cash Account</span>
                                            </button>
                                            <button className="pc-btn-secondary" onClick={() => window.location.hash = '#petty-cash-setup'}>
                                                <Icon d={icons.settings} size={15} color="#475569" />
                                                <span>Configure Names & Locations</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredAccounts.map(acc => (
                                    <tr key={acc._id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Icon d={icons.wallet} size={16} color="#16539C" />
                                                </div>
                                                <strong style={{ color: '#0f172a' }}>{acc.accountName}</strong>
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{ color: '#475569', fontWeight: 500 }}>{acc.location || '—'}</span>
                                        </td>
                                        <td>
                                            {acc.handlerId ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#16539C' }}>
                                                        {acc.handlerId.firstName ? acc.handlerId.firstName[0] : 'U'}
                                                    </div>
                                                    <span style={{ fontWeight: 600, color: '#334155' }}>{acc.handlerId.firstName} {acc.handlerId.lastName}</span>
                                                </div>
                                            ) : (
                                                <span style={{ color: '#94a3b8' }}>Unassigned</span>
                                            )}
                                        </td>
                                        <td>
                                            <span style={{ fontWeight: 800, color: acc.currentBalance < acc.fundLimit * 0.2 ? '#dc2626' : '#0f172a' }}>
                                                {formatCurrency(acc.currentBalance, country)}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ color: '#64748b' }}>{formatCurrency(acc.fundLimit, country)}</span>
                                        </td>
                                        <td>
                                            <span className={`pc-status-badge ${acc.status === 'Active' ? 'status-active' : 'status-inactive'}`}>
                                                {acc.status || 'Active'}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button
                                                className="pc-btn-secondary"
                                                style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700 }}
                                                onClick={() => setModal({ type: 'add-transaction', account: acc })}
                                            >
                                                <Icon d={icons.plus} size={13} color="#475569" /> Record Entry
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Transactions Panel */}
            <div className="pc-panel">
                <div className="pc-panel-header">
                    <h3 className="pc-panel-title">Recent Transactions</h3>
                    <div className="pc-search-wrap">
                        <Icon d={icons.search} size={15} />
                        <input
                            className="pc-search-input"
                            placeholder="Search transactions..."
                            value={transSearch}
                            onChange={e => setTransSearch(e.target.value)}
                        />
                    </div>
                </div>
                <div className="pc-table-wrap">
                    <table className="pc-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Account</th>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Balance After</th>
                                <th>Handler</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="pc-empty-state">
                                        <p>Loading transactions...</p>
                                    </td>
                                </tr>
                            ) : filteredTransactions.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="pc-empty-state">
                                        <p>No transactions recorded yet.</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredTransactions.map(t => (
                                    <tr key={t._id}>
                                        <td style={{ color: '#475569', fontWeight: 500 }}>
                                            {t.date ? new Date(t.date).toLocaleDateString('en-GB') : '—'}
                                        </td>
                                        <td>
                                            <strong style={{ color: '#16539C' }}>{t.accountId?.accountName || '—'}</strong>
                                        </td>
                                        <td>
                                            <span style={{
                                                padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                                background: t.type === 'Fund Addition' ? '#ecfdf5' : (t.type === 'Withdrawal' ? '#fef2f2' : '#f1f5f9'),
                                                color: t.type === 'Fund Addition' ? '#059669' : (t.type === 'Withdrawal' ? '#dc2626' : '#475569'),
                                                border: `1px solid ${t.type === 'Fund Addition' ? '#a7f3d0' : (t.type === 'Withdrawal' ? '#fecaca' : '#e2e8f0')}`
                                            }}>
                                                {t.type}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ fontWeight: 800, color: (t.type === 'Withdrawal' || t.type === 'Adjustment (Out)') ? '#dc2626' : '#059669' }}>
                                                {(t.type === 'Withdrawal' || t.type === 'Adjustment (Out)') ? '-' : '+'} {formatCurrency(t.amount, country)}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: 600, color: '#334155' }}>{formatCurrency(t.balanceAfter, country)}</td>
                                        <td style={{ color: '#475569' }}>
                                            {t.handlerId ? `${t.handlerId.firstName || ''} ${t.handlerId.lastName || ''}` : '—'}
                                        </td>
                                        <td style={{ color: '#64748b', maxWidth: 260 }}>{t.description || '—'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modals */}
            {modal === 'add-account' && (
                <AddAccountModal
                    names={accountNames}
                    locations={locations}
                    staff={staff}
                    onClose={() => setModal(null)}
                    onSave={handleAddAccount}
                />
            )}

            {modal?.type === 'add-transaction' && (
                <AddTransactionModal
                    account={modal.account}
                    types={transactionTypes}
                    staff={staff}
                    onClose={() => setModal(null)}
                    onSave={handleAddTransaction}
                />
            )}
        </div>
    );
}

function AddAccountModal({ names, locations, staff, onClose, onSave }) {
    const [form, setForm] = useState({ accountNameId: '', locationId: '', handlerId: '', openingBalance: 0, fundLimit: 5000 });

    const handleSubmit = (e) => {
        e.preventDefault();
        const nameObj = names.find(n => n._id === form.accountNameId);
        const locObj = locations.find(l => l._id === form.locationId);
        onSave({
            ...form,
            accountName: nameObj?.name || '',
            location: locObj?.name || ''
        });
    };

    return (
        <div className="pc-modal-overlay" onClick={onClose}>
            <div className="pc-modal-dialog" onClick={e => e.stopPropagation()}>
                <div className="pc-modal-header">
                    <h3>Add Petty Cash Account</h3>
                    <button className="pc-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="pc-modal-body">
                        <div className="pc-form-group">
                            <label className="pc-form-label">ACCOUNT NAME *</label>
                            <select className="pc-form-select" required value={form.accountNameId} onChange={e => setForm({ ...form, accountNameId: e.target.value })}>
                                <option value="">Select Account Name</option>
                                {names.map(n => <option key={n._id} value={n._id}>{n.name}</option>)}
                            </select>
                        </div>
                        <div className="pc-form-group">
                            <label className="pc-form-label">LOCATION *</label>
                            <select className="pc-form-select" required value={form.locationId} onChange={e => setForm({ ...form, locationId: e.target.value })}>
                                <option value="">Select Location</option>
                                {locations.map(l => <option key={l._id} value={l._id}>{l.name}</option>)}
                            </select>
                        </div>
                        <div className="pc-form-group">
                            <label className="pc-form-label">HANDLER / CUSTODIAN (STAFF) *</label>
                            <select className="pc-form-select" required value={form.handlerId} onChange={e => setForm({ ...form, handlerId: e.target.value })}>
                                <option value="">Assign to Staff Member</option>
                                {staff.map(s => <option key={s._id} value={s._id}>{s.firstName} {s.lastName}</option>)}
                            </select>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div className="pc-form-group">
                                <label className="pc-form-label">OPENING BALANCE</label>
                                <input type="number" step="0.01" className="pc-form-input" value={form.openingBalance} onChange={e => setForm({ ...form, openingBalance: e.target.value })} />
                            </div>
                            <div className="pc-form-group">
                                <label className="pc-form-label">FUND LIMIT</label>
                                <input type="number" step="0.01" className="pc-form-input" value={form.fundLimit} onChange={e => setForm({ ...form, fundLimit: e.target.value })} />
                            </div>
                        </div>
                    </div>
                    <div className="pc-modal-footer">
                        <button type="button" className="pc-btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className="pc-btn-primary">Create Account</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function AddTransactionModal({ account, types, staff, onClose, onSave }) {
    const [form, setForm] = useState({ accountId: account._id, type: 'Withdrawal', amount: '', handlerId: '', description: '', date: new Date().toISOString().split('T')[0] });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(form);
    };

    return (
        <div className="pc-modal-overlay" onClick={onClose}>
            <div className="pc-modal-dialog" onClick={e => e.stopPropagation()}>
                <div className="pc-modal-header">
                    <h3>Record Entry: {account.accountName}</h3>
                    <button className="pc-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="pc-modal-body">
                        <div className="pc-form-group">
                            <label className="pc-form-label">TRANSACTION TYPE *</label>
                            <select className="pc-form-select" required value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                                <option value="Withdrawal">Withdrawal (Expense)</option>
                                <option value="Fund Addition">Fund Addition</option>
                                <option value="Adjustment (In)">Adjustment (In)</option>
                                <option value="Adjustment (Out)">Adjustment (Out)</option>
                            </select>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div className="pc-form-group">
                                <label className="pc-form-label">AMOUNT *</label>
                                <input type="number" step="0.01" className="pc-form-input" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} autoFocus />
                            </div>
                            <div className="pc-form-group">
                                <label className="pc-form-label">DATE *</label>
                                <input type="date" className="pc-form-input" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
                            </div>
                        </div>
                        <div className="pc-form-group">
                            <label className="pc-form-label">HANDLER (STAFF) *</label>
                            <select className="pc-form-select" required value={form.handlerId} onChange={e => setForm({ ...form, handlerId: e.target.value })}>
                                <option value="">Select Staff</option>
                                {staff.map(s => <option key={s._id} value={s._id}>{s.firstName} {s.lastName}</option>)}
                            </select>
                        </div>
                        <div className="pc-form-group">
                            <label className="pc-form-label">DESCRIPTION</label>
                            <input className="pc-form-input" placeholder="e.g. Fuel, Office Supplies..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                        </div>
                    </div>
                    <div className="pc-modal-footer">
                        <button type="button" className="pc-btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className="pc-btn-primary">Record Transaction</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
